import { formatNumberWithSpaces } from "@/composables/formatUtils";
import type { CommitmentPoolRow, SalesPlan } from "@/forwards2/forwardsTypes";
import { computed, type Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Volume an override takes beyond one pool's sales strategy in one period — the line of the back or
 * resize dialog's override banner.
 *
 * @property volumeMwh - Exact: the take minus what the strategy lets the pool sell there today
 * @property nextReleaseOn - ISO date the pool's next window opens there; null once fully released
 */
export interface StrategyOverage {
  poolName: string;
  periodLabel: string;
  volumeMwh: number;
  salesPlan: SalesPlan;
  nextReleaseOn: string | null;
}

/**
 * The part of a one-period take beyond what the strategy lets the pool sell there today: the back
 * and resize dialogs take one period, so the backend's per-period cap is all it is measured against.
 */
export function beyondStrategyMwh(take: number, sellable: number): number {
  return Math.max(0, take - Math.max(0, sellable));
}

/**
 * Whether the row carries the strategy's figure for the period. Without it what a take puts beyond
 * the strategy is unknown, not zero.
 */
export function hasStrategyFigures(
  pool: CommitmentPoolRow | null,
  periodLabel: string
): pool is CommitmentPoolRow {
  return pool !== null && pool.capacity_by_period[periodLabel] !== undefined;
}

/**
 * What a take of `takeMwh` from one pool in one period puts beyond its strategy, as the back and
 * resize dialogs' banner states it; null within the strategy.
 */
export function commitmentPoolOverage(
  pool: CommitmentPoolRow,
  periodLabel: string,
  takeMwh: number
): StrategyOverage | null {
  const capacity = pool.capacity_by_period[periodLabel];
  const volumeMwh = beyondStrategyMwh(takeMwh, capacity?.sellable_mwh ?? 0);
  if (volumeMwh <= 0) return null;
  return {
    poolName: pool.name,
    periodLabel,
    volumeMwh,
    salesPlan: pool.sales_plan,
    nextReleaseOn: capacity?.next_release?.opens_on ?? null,
  };
}

/**
 * The sales strategy override as the back and resize dialogs hold it: the box, unticked whenever
 * the dialog opens, and while it is ticked, what the take puts beyond its pool's strategy
 * (`measure`), which turns the save red. Only a super admin opens either dialog, since every deal
 * write is theirs, so the override needs no permission of its own.
 */
export function useStrategyOverride(
  open: Ref<boolean>,
  measure: () => StrategyOverage | null
) {
  const ignored = ref(false);

  const overage = computed(() => (ignored.value ? measure() : null));
  const beyondStrategy = computed(() => overage.value !== null);
  const submitVariant = computed(() =>
    beyondStrategy.value ? "btn-danger" : "btn-primary"
  );

  watch(open, (isOpen) => {
    if (isOpen) ignored.value = false;
  });

  return { ignored, overage, beyondStrategy, submitVariant };
}

/**
 * How the sales strategy reads in running text: "four times a year, next year's production" or
 * "twice a year, 25% two years ahead", a long release date, the override banner's line for one
 * pool, and why a pool is held back.
 */
export const useSalesStrategyText = () => {
  const { t, locale } = useI18n();

  const summary = (plan: SalesPlan) =>
    t("forwards.sales_strategy.summary", {
      split: t(`forwards.sales_strategy.summary_split.${plan.strategy}`),
      horizon:
        plan.early_share_pct === 0
          ? t("forwards.sales_strategy.summary_next_year")
          : t("forwards.sales_strategy.summary_ahead", {
              percent: plan.early_share_pct,
            }),
    });

  /** "1 January 2027". Windows open on UTC days, so the date is read and printed in UTC. */
  const releaseDate = (isoDate: string): string => {
    const date = new Date(`${isoDate}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return isoDate;
    // Bare "en" formats month-first; the English copy reads day-first like the rest of the app.
    const tag = locale.value === "en" ? "en-GB" : locale.value;
    return new Intl.DateTimeFormat(tag, {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  };

  const overageLine = (overage: StrategyOverage): string => {
    const values = {
      subject: t("forwards.sales_strategy.override.subject", {
        pool: overage.poolName,
        period: overage.periodLabel,
      }),
      mwh: formatNumberWithSpaces(overage.volumeMwh),
      strategy: summary(overage.salesPlan),
    };
    return overage.nextReleaseOn === null
      ? t("forwards.sales_strategy.override.beyond_line", values)
      : t("forwards.sales_strategy.override.beyond_line_next_release", {
          ...values,
          date: releaseDate(overage.nextReleaseOn),
        });
  };

  /** Why a pool with available volume offers none yet, as a left-out list reason. */
  const heldBackReason = (nextReleaseOn: string | null): string =>
    nextReleaseOn === null
      ? t("forwards.sales_strategy.held_back_reason")
      : t("forwards.sales_strategy.held_back_reason_until", {
          date: releaseDate(nextReleaseOn),
        });

  return { summary, releaseDate, overageLine, heldBackReason };
};
